import {
  DEFAULT_HUMAN_HANDOFF_CONFIG,
  DEFAULT_AGENT_RESPONSE_MODE,
  DEFAULT_OUTPUT_PRESENTATION_POLICY,
} from "@/types"
import type { GeneratedAgentTemplateDraftResponse } from "@/hooks/api"
import {
  transToHumanHandoffConfig,
  transToUnifiedTool,
} from "@/hooks/api/types"

import type {
  AgentTemplateDefinition,
  AgentTemplateDraft,
  AgentTemplateFormItem,
  AgentTemplateFormValue,
  AgentTemplateFormValues,
  AgentTemplateLocale,
  AgentTemplateToolFieldDefinition,
} from "./types"
import { resolveLocalizedText } from "./registry"

const getInitialValue = (
  item: AgentTemplateFormItem,
  locale: AgentTemplateLocale
): AgentTemplateFormValue | undefined => {
  if ("defaultValue" in item && item.defaultValue != null) {
    return item.defaultValue
  }
  if (item.type === "agent_model") {
    return {
      enabled: item.required === true || Boolean(item.defaultModelId),
      ...(item.defaultModelId ? { modelId: item.defaultModelId } : {}),
    }
  }
  if (item.type === "tool_text_to_speech") {
    return {
      enabled: item.required === true || item.enabledByDefault === true,
      configuration: {
        language: item.defaultLanguage,
        slowEnabled: item.slowEnabledByDefault,
      },
    }
  }
  if (item.type === "tool_request_form" || item.type === "tool_human_handoff") {
    return {
      enabled: item.required === true || item.enabledByDefault === true,
      configuration: {
        request: item.defaultRequest
          ? resolveLocalizedText(item.defaultRequest, locale)
          : "",
        ...(item.type === "tool_human_handoff" ? { alwaysOn: false } : {}),
      },
    }
  }
  if (item.type.startsWith("tool_")) {
    const toolItem = item as AgentTemplateToolFieldDefinition
    return {
      enabled: toolItem.required === true || toolItem.enabledByDefault === true,
      configuration: {},
    }
  }
  return undefined
}

export const getInitialAnswers = (
  template: AgentTemplateDefinition,
  locale: AgentTemplateLocale
): AgentTemplateFormValues => {
  return template.form.reduce<AgentTemplateFormValues>((defaults, field) => {
    if (!("key" in field)) return defaults
    const initialValue = getInitialValue(field, locale)
    if (initialValue !== undefined) defaults[field.key] = initialValue
    return defaults
  }, {})
}

export const generatedResponseToTemplateResult = (
  response: GeneratedAgentTemplateDraftResponse
): AgentTemplateDraft => ({
  name: response.draft.name,
  description: response.draft.description,
  systemPrompt: response.draft.system_prompt,
  unifiedTools: response.draft.unified_tools.map(transToUnifiedTool),
  knowledgeNodeIds: [],
  llmModel: response.draft.llm_model,
  llmModelSettings: {},
  attachments: [],
  suggestedQuestions: response.draft.suggested_questions,
  responseMode: response.draft.response_mode ?? DEFAULT_AGENT_RESPONSE_MODE,
  humanHandoff: response.draft.human_handoff
    ? transToHumanHandoffConfig(response.draft.human_handoff)
    : { ...DEFAULT_HUMAN_HANDOFF_CONFIG },
  aiTakeoverGreeting: "",
  outputPresentationPolicy: { ...DEFAULT_OUTPUT_PRESENTATION_POLICY },
  pendingKnowledgeBindings: response.draft.pending_knowledge_bindings || [],
})
